import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';

const companyQuery = (companyId) => ({
  queryKey: ['company', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(`/companies/${companyId}`);
    return data;
  },
});

export const loader = (queryClient) => async () => {
  const userData = await queryClient.ensureQueryData({
    queryKey: ['user'],
    queryFn: async () => {
      const { data } = await customFetch.get('/users/current-user');
      return data;
    },
  });
  const companyId = userData.user.company;
  if (!companyId) return {};
  await queryClient.ensureQueryData(companyQuery(companyId));
  return { companyId };
};

const CompanySettings = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(companyQuery(companyId));
  const company = data?.company;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    street: '',
    city: '',
    zip: '',
    country: 'Schweiz',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        website: company.website || '',
        street: company.address?.street || '',
        city: company.address?.city || '',
        zip: company.address?.zip || '',
        country: company.address?.country || 'Schweiz',
      });
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await customFetch.patch(`/companies/${companyId}`, {
        name: formData.name,
        email: formData.email,
        website: formData.website,
        address: {
          street: formData.street,
          city: formData.city,
          zip: formData.zip,
          country: formData.country,
        },
      });
      queryClient.invalidateQueries(['company', companyId]);
      toast.success('Firmeneinstellungen gespeichert');
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-svisit-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Firmeneinstellungen
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Allgemeine Informationen deiner Organisation.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-lg">
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Firmenname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  E-Mail
                </label>
                <div className="mt-2">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Website
                </label>
                <div className="mt-2">
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.firma.ch"
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <h3 className="text-sm font-medium leading-6 text-gray-900 mb-4 mt-2 border-t border-gray-200 pt-6">
                  Adresse
                </h3>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Strasse
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  PLZ
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Ort
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Land
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-4 py-4 sm:px-8 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {saving ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompanySettings;
